import './PaymentSupport.css';
import MoreOptionsMenu from 'src/Components/MoreOptions/MoreOptions';
import { InfoMessageType } from 'src/Components/InfoBar/InfoBar';
import { ILicensesInfo, SubscriptionType } from 'src/Utils/Hooks/ManageLicenses/useGetLicensesInfo';
import CancelSubscription from '../CancelSubscription/CancelSubscription';
import UpgradeSubscription from '../UpgradeSubscription/UpgradeSubscription';
import EditPaymentMethod from '../EditPaymentMethod/EditPaymentMethod';
import Resubscribe from '../Resubscribe/Resubscribe';
import { useGetPaymentProducts } from 'src/Utils/Hooks/Payments/useGetPaymentProducts';
import { useCallback, useEffect } from 'react';
import { logger } from 'src/Utils/LoggerService/LoggerService';
import CurrentSubscription from '../CurrentSubscription/CurrentSubscription';
import LicenseTitle from '../../LicenseTitle/LicenseTitle';

interface IPaymentSupport{
    isSuiteLicense: boolean;
    showPaymentOption: boolean;
    containsSuiteLicense: boolean;
    clinicId: string;
    setInfo: (message: string, type: InfoMessageType) => void;
    updatePageLoader: (load: boolean) => void;
    licensesInfo: ILicensesInfo;
    clinicName: string;
    isClinicAdmin: boolean;
    isPermanent: boolean;
}

const PaymentSupport: React.FC<IPaymentSupport> = ({isSuiteLicense, showPaymentOption, containsSuiteLicense, clinicId, setInfo, updatePageLoader, licensesInfo, clinicName, isClinicAdmin, isPermanent = false}) => {
    const {products, reqestError, reqestLoading, getPaymentProducts} = useGetPaymentProducts();
    
    useEffect(() => {
        updatePageLoader(reqestLoading);
    }, [reqestLoading, updatePageLoader]);

    useEffect(() => {
        if (reqestError !== '') {
            setInfo(reqestError, InfoMessageType.error);
        } 
    },[reqestError, setInfo]);

    useEffect(() => {
        if (licensesInfo) {
           getPaymentProducts(clinicId);
        }
    }, [licensesInfo, clinicId, getPaymentProducts]);

    const setMessageInfo = useCallback((message: string, type: InfoMessageType) => {
        setInfo(message, type);
        logger.logInformation(`Payment support message: ${message}, type: ${type}`);
    },[setInfo]);

    const isSubscriptionCancelled = () => 
            licensesInfo?.clinicPaymentTransactions?.length > 0 &&
            licensesInfo.clinicPaymentTransactions[0].subscriptionType === SubscriptionType.Cancel;
    
    const isSubscriptionDateExpired = () => {
        const endDate = licensesInfo?.clinicUserAndStorageLicenseInfo?.licenseEndDate;
        if (!endDate) return false;
        var updateCurrentDate = new Date(new Date().getTime() + 2 * 60 * 60 * 1000);
        logger.logInformation(`Resubscribe license - Current Date: ${updateCurrentDate}, End Date: ${endDate}`);
        return new Date(endDate) < updateCurrentDate; // Check if end date is before today
    }

    return (
        <>
            <div className="d-flex align-items-center">
                 <LicenseTitle
                 licensesInfo={licensesInfo}
                            isSuiteLicense={isSuiteLicense}
                            showPaymentOption={showPaymentOption}
                            containsSuiteLicense={containsSuiteLicense} />
                
                <div className="right-align-container">
                    <div>
                        {
                            products &&
                            <UpgradeSubscription 
                                isPermanent = {isPermanent}
                                clinicId={clinicId}
                                setInfo={setInfo}
                                updatePageLoader={updatePageLoader}
                                isUpgradeDisabled={isSubscriptionCancelled() || !isClinicAdmin}
                                products={products}
                                refreshProducts={getPaymentProducts}
                            />
                        }
                    </div>
                    <div>
                        <MoreOptionsMenu id='license-taskbar-management' dropdownButtonActiveBackgroundColor="var(--color-primary-translucent)" dropdownButtonTextColor="var(--primary-color)" iconFontSize='28px' iconMarginTop='3px' disabled={!isClinicAdmin}>
                            <ul className="dropdown-menu dropdown-menu-end taskbar" aria-labelledby="dropdownMenuButton1" style={{width: '208px'}}>
                                {
                                    !isSubscriptionCancelled() &&
                                    <li className={(isSubscriptionCancelled() || !isClinicAdmin) ? 'disabled-li' : ''}>
                                        <CancelSubscription
                                            clinicId={clinicId}
                                            setInfo={setInfo}
                                            updatePageLoader={updatePageLoader}
                                            licensesInfo={licensesInfo}
                                            clinicName={clinicName}
                                        />
                                    </li>
                                }
                                {
                                    !isSubscriptionCancelled() &&
                                    <li className={!isClinicAdmin ? 'disabled-li' : ''}>
                                        <EditPaymentMethod 
                                            clinicId={clinicId}
                                            setInfo={setInfo}
                                            updatePageLoader={updatePageLoader}
                                        />
                                    </li>
                                }
                                {
                                    isSubscriptionCancelled() && products?.currentPriceId &&
                                    <li className={!isClinicAdmin ? 'disabled-li' : ''}>
                                        <Resubscribe
                                            clinicId={clinicId}
                                            setInfo={setInfo}
                                            updatePageLoader={updatePageLoader}
                                            priceId={products?.currentPriceId}
                                            isSubscriptionExpired={isSubscriptionDateExpired()}
                                        />
                                    </li>
                                }
                            </ul>
                        </MoreOptionsMenu>
                    </div>
                </div>
            </div>
            {products &&
             <div className='mt-3'>
                <CurrentSubscription 
                    purchaseDate= {licensesInfo.clinicUserAndStorageLicenseInfo.activationDateInAura}
                    clinicId={clinicId}
                    products={products}
                    setInfo={setMessageInfo}
                    updatePageLoader={updatePageLoader}
                    isClinicAdmin={isClinicAdmin}
                />
            </div>
      }
        </>
    );
}

export default PaymentSupport;